import { useParams } from "react-router-dom"
import { useEffect, useRef, useState, useMemo } from "react"
import { GoogleMap, Marker, DirectionsRenderer, TrafficLayer, useJsApiLoader } from "@react-google-maps/api"
import { Typography, CircularProgress, Avatar, Dialog, DialogTitle, DialogContent, DialogContentText, IconButton } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import RoomIcon from "@mui/icons-material/Room"
import FlagIcon from "@mui/icons-material/Flag"
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar"
import PhoneIcon from "@mui/icons-material/Phone"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import NavigationIcon from "@mui/icons-material/Navigation"
import MyLocationIcon from "@mui/icons-material/MyLocation"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import CloseIcon from "@mui/icons-material/Close"

const useStyles = makeStyles((theme) => ({
  root: { position: "relative", height: "100vh", width: "100vw", fontFamily: theme.typography.fontFamily },
  // Emulated fullscreen for browsers without the Fullscreen API (iOS Safari)
  rootFullscreen: {
    position: "fixed", top: 0, left: 0, width: "100vw",
    height: "100dvh", fallbacks: { height: "100vh" },
    zIndex: 1300, background: "#fff"
  },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" },
  error: {
    display: "flex", justifyContent: "center", alignItems: "center", height: "100vh",
    background: "#f5f7fa", padding: 24, fontFamily: theme.typography.fontFamily
  },
  errorCard: {
    textAlign: "center", maxWidth: 360, width: "100%",
    background: "#fff", borderRadius: 20, padding: "40px 32px 32px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)"
  },
  errorIcon: { fontSize: 64, color: "#e0e0e0", marginBottom: 16 },
  errorTitle: { fontSize: 20, fontWeight: 700, color: "#1a1a1a", marginBottom: 8, fontFamily: theme.typography.fontFamily },
  errorMsg: { fontSize: 14, color: "#888", marginBottom: 28, lineHeight: 1.5, fontFamily: theme.typography.fontFamily },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "10px 24px", borderRadius: 12,
    background: "#1a73e8", color: "#fff", border: "none",
    fontSize: 14, fontWeight: 600, cursor: "pointer",
    fontFamily: theme.typography.fontFamily,
    transition: "background 0.2s"
  },
  retryBtn: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "10px 24px", borderRadius: 12,
    background: "#f0f0f0", color: "#555", border: "none",
    fontSize: 14, fontWeight: 600, cursor: "pointer", marginLeft: 12,
    fontFamily: theme.typography.fontFamily,
    transition: "background 0.2s"
  },
  card: {
    position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
    width: "calc(100% - 32px)", maxWidth: 420,
    background: "#fff", borderRadius: 24,
    padding: "20px 20px 24px", boxShadow: "0 -4px 24px rgba(0,0,0,0.15)",
    maxHeight: "40vh", overflowY: "auto", zIndex: 10
  },
  navBanner: {
    position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
    display: "flex", alignItems: "center", gap: 12,
    background: "#1a73e8", color: "#fff", borderRadius: 999,
    padding: "10px 18px", boxShadow: "0 4px 16px rgba(26,115,232,0.4)",
    zIndex: 10, maxWidth: "calc(100% - 32px)"
  },
  navEta: { fontSize: 18, fontWeight: 700, lineHeight: 1.1, whiteSpace: "nowrap" },
  navSub: { fontSize: 12, opacity: 0.9, whiteSpace: "nowrap" },
  completedBanner: {
    position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
    display: "flex", alignItems: "center", gap: 10,
    background: "#4caf50", color: "#fff", borderRadius: 999,
    padding: "10px 18px", boxShadow: "0 4px 16px rgba(76,175,80,0.4)",
    zIndex: 10, maxWidth: "calc(100% - 32px)",
    fontSize: 15, fontWeight: 700, whiteSpace: "nowrap"
  },
  cancelledBanner: {
    position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
    display: "flex", alignItems: "center", gap: 10,
    background: "#f44336", color: "#fff", borderRadius: 999,
    padding: "10px 18px", boxShadow: "0 4px 16px rgba(244,67,54,0.4)",
    zIndex: 10, maxWidth: "calc(100% - 32px)",
    fontSize: 15, fontWeight: 700, whiteSpace: "nowrap"
  },
  recenterBtn: {
    position: "absolute", top: 16, right: 16,
    width: 48, height: 48, borderRadius: "50%",
    background: "#fff", border: "none", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.25)", zIndex: 10, transition: "background 0.2s"
  },
  fullscreenBtn: {
    position: "absolute", top: 76, right: 16,
    width: 48, height: 48, borderRadius: "50%",
    background: "#fff", border: "none", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.25)", zIndex: 10, transition: "background 0.2s"
  },
  statusBadge: {
    display: "inline-flex", alignItems: "center", padding: "4px 12px", borderRadius: 20,
    fontSize: 12, fontWeight: 600, letterSpacing: 0.3
  },
  driverRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
  driverName: { fontSize: 16, fontWeight: 700, color: "#1a1a1a" },
  driverPhone: { fontSize: 13, color: "#666", marginTop: 2 },
  vehicleTag: {
    display: "inline-block", padding: "3px 10px", borderRadius: 8,
    background: "#e8f0fe", color: "#1a73e8", fontSize: 12, fontWeight: 500,
    marginRight: 6, marginBottom: 4
  },
  addressRow: { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 },
  addressIcon: { marginTop: 2, flexShrink: 0 },
  addressText: { fontSize: 14, color: "#333", lineHeight: 1.4 },
  addressLabel: { fontSize: 11, color: "#999", textTransform: "uppercase", fontWeight: 600, marginBottom: 2 },
  divider: { height: 1, background: "#f0f0f0", margin: "14px 0" },
}))

const STATUS_MAP = {
  SCHEDULED: { bg: "#e8f5e9", color: "#2e7d32", label: "Scheduled" },
  DRIVER_ACCEPTED: { bg: "#e8f5e9", color: "#2e7d32", label: "Scheduled" },
  IN_PROGRESS: { bg: "#e3f2fd", color: "#1565c0", label: "In Progress" },
  COMPLETED: { bg: "#f3e5f5", color: "#7b1fa2", label: "Completed" },
  CANCELLED: { bg: "#fbe9e7", color: "#c62828", label: "Cancelled" },
}

// Clean, low-clutter navigation map style
const MAP_STYLE = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#d4e6f1" }] },
]
// Pickup marker SVG — green pin
const PICKUP_SVG = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
    <path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.06 27.94 0 18 0z" fill="#4CAF50" stroke="#fff" stroke-width="2"/>
    <circle cx="18" cy="17" r="6" fill="#fff"/>
  </svg>`
)

// Drop-off marker SVG — red pin
const DROPOFF_SVG = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
    <path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.06 27.94 0 18 0z" fill="#F44336" stroke="#fff" stroke-width="2"/>
    <rect x="12" y="14" width="12" height="12" rx="2" fill="#fff"/>
  </svg>`
)
const carIcon = (deg) => ({
  url:
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
        <g transform="rotate(${deg} 22 22)">
          <ellipse cx="22" cy="24" rx="15" ry="15" fill="#1a73e8" fill-opacity="0.15"/>
          <rect x="14" y="8" width="16" height="28" rx="6" fill="#1a73e8" stroke="#ffffff" stroke-width="2"/>
          <rect x="16.5" y="11" width="11" height="7" rx="3" fill="#cfe0fb"/>
          <rect x="16.5" y="27" width="11" height="5" rx="2" fill="#0d47a1" fill-opacity="0.6"/>
          <rect x="11" y="14" width="3.5" height="6" rx="1.5" fill="#0d3b7a"/>
          <rect x="29.5" y="14" width="3.5" height="6" rx="1.5" fill="#0d3b7a"/>
          <rect x="11" y="25" width="3.5" height="6" rx="1.5" fill="#0d3b7a"/>
          <rect x="29.5" y="25" width="3.5" height="6" rx="1.5" fill="#0d3b7a"/>
        </g>
      </svg>`
    ),
  scaledSize: new window.google.maps.Size(44, 44),
  anchor: new window.google.maps.Point(22, 22),
})

const toRad = (d) => (d * Math.PI) / 180
const toDeg = (r) => (r * 180) / Math.PI

// Bearing (0-360°) from point A to point B
const computeBearing = (from, to) => {
  const dLng = toRad(to.lng - from.lng)
  const y = Math.sin(dLng) * Math.cos(toRad(to.lat))
  const x =
    Math.cos(toRad(from.lat)) * Math.sin(toRad(to.lat)) -
    Math.sin(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.cos(dLng)
  return (toDeg(Math.atan2(y, x)) + 360) % 360
}

const TripTracking = () => {
  const { trackingUuid } = useParams()
  const cls = useStyles()
  const [group, setGroup] = useState(null)
  const [pickup, setPickup] = useState(null)
  const [dropoff, setDropoff] = useState(null)
  const [driverLoc, setDriverLoc] = useState(null)
  const [displayLoc, setDisplayLoc] = useState(null) // interpolated marker position
  const [heading, setHeading] = useState(0)
  const [error, setError] = useState(null)
  const [directions, setDirections] = useState(null)
  const [eta, setEta] = useState(null)
  const [followDriver, setFollowDriver] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [stops, setStops] = useState([])
  const [stopsModalOpen, setStopsModalOpen] = useState(false)

  const mapRef = useRef(null)
  const containerRef = useRef(null)
  const prevLocRef = useRef(null)
  const animRef = useRef(null)
  const didFitRef = useRef(false)

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "",
  })

  const pickupIcon = useMemo(() => isLoaded ? {
    url: PICKUP_SVG,
    scaledSize: new window.google.maps.Size(36, 48),
    anchor: new window.google.maps.Point(18, 48),
  } : null, [isLoaded])

  const dropoffIcon = useMemo(() => isLoaded ? {
    url: DROPOFF_SVG,
    scaledSize: new window.google.maps.Size(36, 48),
    anchor: new window.google.maps.Point(18, 48),
  } : null, [isLoaded])

  const pickupPos = pickup?.latitude && pickup?.longitude ? { lat: pickup.latitude, lng: pickup.longitude } : null
  const dropoffPos = dropoff?.latitude && dropoff?.longitude ? { lat: dropoff.latitude, lng: dropoff.longitude } : null
  const pickupDone = pickup?.status === 4 || pickup?.status === 5  // COMPLETED or NO_SHOW
  const pickupArrived = pickup?.status === 3  // ARRIVED
  const mapCenter = displayLoc || driverLoc || pickupPos || dropoffPos || { lat: 39.7392, lng: -104.9903 }

  const fetchData = () => {
    fetch(`/trips/api/v1/public/track/${trackingUuid}/`)
      .then(r => {
        if (!r.ok) throw new Error(`Ride not found (${r.status})`)
        const contentType = r.headers.get("content-type") || ""
        if (!contentType.includes("application/json")) throw new Error("Ride not found")
        return r.json()
      })
      .then(response => {
        const data = response.data || response
        if (!data.group) throw new Error(data.error || "Ride not found")
        setGroup(data.group)
        setPickup(data.pickup || null)
        setDropoff(data.dropoff || null)
        setStops(data.stops || [])
        if (data.group?.driver_location) setDriverLoc(data.group.driver_location)
      })
      .catch(e => {
        const msg = e.message
        setError(msg === "Failed to fetch" ? "Unable to connect. Please check your internet." : msg)
      })
  }

  useEffect(() => { fetchData() }, [trackingUuid])

  // Ably for live updates — subscribe DIRECTLY to the driver's own channel
  // (`driver:{id}`), the same channel the driver's mobile app publishes to.
  // No backend relay needed.
  useEffect(() => {
    const apiKey = process.env.REACT_APP_ABLY_API_KEY
    const driverId = group?.driver?.id
    // Ably is loaded from the CDN script tag in public/index.html (window.Ably)
    const Ably = typeof window !== "undefined" ? window.Ably : null
    if (!apiKey || !driverId || !Ably) return

    const ably = new Ably.Realtime({ key: apiKey })
    const channel = ably.channels.get(`driver:${driverId}`)
    let cancelled = false

    channel.subscribe("location", (msg) => {
      if (cancelled) return
      const data = msg?.data || {}
      if (data.latitude != null && data.longitude != null) {
        setDriverLoc({ lat: Number(data.latitude), lng: Number(data.longitude) })
      }
    })

    return () => {
      cancelled = true
      try { channel.unsubscribe() } catch (e) { /* ignore */ }
      try { ably.close() } catch (e) { /* ignore */ }
    }
  }, [trackingUuid, group?.driver?.id])

  // Smoothly animate the driver marker between location updates + compute heading
  useEffect(() => {
    if (!driverLoc?.lat || !driverLoc?.lng) return
    const from = prevLocRef.current || driverLoc
    const to = driverLoc

    if (from.lat !== to.lat || from.lng !== to.lng) {
      setHeading(computeBearing(from, to))
    }

    const start = performance.now()
    const duration = 1000
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1)
      setDisplayLoc({
        lat: from.lat + (to.lat - from.lat) * t,
        lng: from.lng + (to.lng - from.lng) * t,
      })
      if (t < 1) {
        animRef.current = requestAnimationFrame(step)
      } else {
        prevLocRef.current = to
      }
    }
    cancelAnimationFrame(animRef.current)
    animRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(animRef.current)
  }, [driverLoc])

  // Fetch directions.
  // Before the trip starts: pickup → drop-off (two stops + nav path only).
  // After the driver starts (IN_PROGRESS): driver → pickup → drop-off,
  // dropping the pickup waypoint once pickup is complete.
  useEffect(() => {
    if (!isLoaded || !pickupPos || !dropoffPos) { setDirections(null); return }
    const svc = new window.google.maps.DirectionsService()

    const active = group?.status === "IN_PROGRESS" && !!driverLoc
    const origin = active ? driverLoc : pickupPos
    const destination = dropoffPos
    const waypoints = active && !pickupDone ? [{ location: pickupPos, stopover: true }] : []

    svc.route({
      origin,
      destination,
      waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.IMPERIAL,
    }, (result, status) => {
      if (status === "OK") setDirections(result)
    })
  }, [isLoaded, pickup, dropoff, driverLoc, group?.status])

  // Derive ETA / distance to the next stop from the directions result
  useEffect(() => {
    if (!directions) return setEta(null)
    const legs = directions.routes?.[0]?.legs || []
    const nextLeg = legs[0]
    if (!nextLeg) return setEta(null)
    setEta({
      duration: nextLeg.duration?.text,
      distance: nextLeg.distance?.text,
      target: !driverLoc ? "drop-off" : pickupDone ? "drop-off" : pickupArrived ? "arrived" : "pickup",
    })
  }, [directions, driverLoc])

  const fitAll = (mapArg) => {
    const map = mapArg || mapRef.current
    if (!map || !window.google) return
    const bounds = new window.google.maps.LatLngBounds()
    let has = false
    ;[displayLoc || driverLoc, pickupPos, dropoffPos].forEach(p => {
      if (p) { bounds.extend(p); has = true }
    })
    if (has) map.fitBounds(bounds, 80)
  }

  const onMapLoad = (map) => {
    mapRef.current = map
    if (!didFitRef.current) { fitAll(map); didFitRef.current = true }
  }

  // Follow the driver while in follow mode
  useEffect(() => {
    if (followDriver && displayLoc && mapRef.current) {
      mapRef.current.panTo(displayLoc)
    }
  }, [displayLoc, followDriver])

  const handleRecenter = () => {
    setFollowDriver(true)
    if (displayLoc || driverLoc) {
      mapRef.current?.panTo(displayLoc || driverLoc)
      mapRef.current?.setZoom(16)
    } else {
      fitAll()
    }
  }

  const toggleFullscreen = () => {
    const el = containerRef.current
    if (!el) return
    const request = el.requestFullscreen || el.webkitRequestFullscreen
    if (!request) {
      // iOS Safari: no Fullscreen API on elements — emulate with fixed positioning
      setIsFullscreen(prev => !prev)
      return
    }
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      request.call(el)
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document)
    }
  }

  // Listen for fullscreen change to sync state (native mode only;
  // these events never fire on iOS Safari, where state is toggled directly)
  useEffect(() => {
    const handler = () =>
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement))
    document.addEventListener("fullscreenchange", handler)
    document.addEventListener("webkitfullscreenchange", handler)
    return () => {
      document.removeEventListener("fullscreenchange", handler)
      document.removeEventListener("webkitfullscreenchange", handler)
    }
  }, [])

  if (error) {
    const is404 = error.includes("404") || error.includes("not found")
    return (
      <div className={cls.error}>
        <div className={cls.errorCard}>
          <ErrorOutlineIcon className={cls.errorIcon} />
          <div className={cls.errorTitle}>
            {is404 ? "Ride Not Found" : "Something Went Wrong"}
          </div>
          <div className={cls.errorMsg}>
            {is404
              ? "We couldn't find a ride with this tracking link. The ride may have been cancelled or the link may be invalid."
              : error}
          </div>
          <button className={cls.backBtn} onClick={() => { window.location.href = "https://noahcares.com"; }}>
            ← Go Back
          </button>
          <button className={cls.retryBtn} onClick={() => { setError(null); setGroup(null); fetchData(); }}>
            Try Again
          </button>
        </div>
      </div>
    )
  }
  if (!group) return <div className={cls.loading}><CircularProgress /></div>

  const vehicle = group.driver?.vehicle
  const statusKey = (group.status || "").toUpperCase().replace(/[\s-]+/g, "_")
  const statusInfo = STATUS_MAP[statusKey] || { bg: "#eee", color: "#666", label: group.status }
  const driverInitial = group.driver?.name?.charAt(0)?.toUpperCase() || "D"

  // Show the "All Stops" control when more than one client rides, or when an
  // A/M is on board (even a single client then has extra stops to show).
  const clientCount = group?.passenger_count || 0
  const showStopsButton = clientCount > 1 || !!group?.aid_monitor

  const fmtTime = (iso) => {
    if (!iso) return null
    const d = new Date(iso)
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })
  }
  const fmtDate = (iso) => {
    if (!iso) return null
    const d = new Date(iso)
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
  }
  const pickupTime = fmtTime(pickup?.scheduled_time)
  const dropoffTime = fmtTime(dropoff?.scheduled_time)
  const serviceDate = group.service_date ? fmtDate(group.service_date) : null

  return (
    <div className={`${cls.root} ${isFullscreen ? cls.rootFullscreen : ""}`} ref={containerRef}>
      {/* Full-screen Navigation Map */}
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={15}
          onLoad={onMapLoad}
          onDragStart={() => setFollowDriver(false)}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: false,
            clickableIcons: false,
            gestureHandling: "greedy",
            styles: MAP_STYLE,
          }}
        >
          {/* Live traffic conditions */}
          <TrafficLayer />

          {/* Navigation route polyline */}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{ suppressMarkers: true, preserveViewport: true, polylineOptions: { strokeColor: "#1a73e8", strokeWeight: 6, strokeOpacity: 0.9 } }}
            />
          )}

          {/* Pickup */}
          {pickupPos && (
            <Marker position={pickupPos} icon={pickupIcon} zIndex={10} />
          )}

          {/* Dropoff */}
          {dropoffPos && (
            <Marker position={dropoffPos} icon={dropoffIcon} zIndex={10} />
          )}

          {/* Driver live location — rotating top-down car (only in progress) */}
          {group.status === "IN_PROGRESS" && (displayLoc || driverLoc) && (
            <Marker position={displayLoc || driverLoc} zIndex={999} icon={carIcon(heading)} />
          )}
        </GoogleMap>
      )}

      {/* Cancelled banner */}
      {group.status === "CANCELLED" && (
        <div className={cls.cancelledBanner}>
          <CancelIcon style={{ fontSize: 22 }} />
          Trip Cancelled
        </div>
      )}

      {/* Completed banner */}
      {group.status === "COMPLETED" && (
        <div className={cls.completedBanner}>
          <CheckCircleIcon style={{ fontSize: 22 }} />
          Trip Completed
        </div>
      )}

      {/* Top ETA / navigation banner */}
      {(eta || pickupArrived) && group.status === "IN_PROGRESS" && (driverLoc || pickupArrived) && (
        <div className={cls.navBanner}>
          {pickupArrived ? (
            <>
              <CheckCircleIcon style={{ fontSize: 24 }} />
              <div>
                <div className={cls.navEta}>Driver arrived at pickup</div>
                <div className={cls.navSub}>Waiting for pickup</div>
              </div>
            </>
          ) : (
            <>
              <NavigationIcon style={{ fontSize: 24 }} />
              <div>
                <div className={cls.navEta}>{eta.duration || "—"}</div>
                <div className={cls.navSub}>
                  {eta.distance ? `${eta.distance} · ` : ""}to {eta.target}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Fullscreen toggle button */}
      <button
        className={cls.fullscreenBtn}
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen map"}
      >
        {isFullscreen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#666">
            <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#666">
            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
          </svg>
        )}
      </button>

      {/* Recenter / follow button (only in progress) */}
      {isLoaded && group.status === "IN_PROGRESS" && (
        <button
          className={cls.recenterBtn}
          onClick={handleRecenter}
          aria-label="Recenter map"
          style={{ background: followDriver ? "#e8f0fe" : "#fff" }}
        >
          <MyLocationIcon style={{ color: followDriver ? "#1a73e8" : "#666", fontSize: 24 }} />
        </button>
      )}

      {/* Bottom Card */}
      <div className={cls.card}>
        {/* Status + Driver header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <Typography style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a" }}>
            Ride Status
          </Typography>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className={cls.statusBadge} style={{ background: statusInfo.bg, color: statusInfo.color }}>
              {statusInfo.label}
            </span>
            <span
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: 18, cursor: "pointer", color: "#666",
                transition: "transform 0.2s", display: "inline-block",
                transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
                userSelect: "none",
              }}
            >
              ▼
            </span>
          </div>
        </div>

        {!collapsed && (
        <>
        {/* ETA row (in-card mirror for context) */}
        {eta && group.status === "IN_PROGRESS" && driverLoc && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}>
            <AccessTimeIcon style={{ fontSize: 16, color: "#1a73e8" }} />
            <Typography style={{ fontSize: 13, color: "#333" }}>
              Arriving at {eta.target} in <b>{eta.duration}</b>{eta.distance ? ` · ${eta.distance}` : ""}
            </Typography>
          </div>
        )}

        {/* Driver */}
        {group.driver && (
          <div className={cls.driverRow}>
            <Avatar
              src={group.driver?.avatar || undefined}
              style={{ width: 48, height: 48, background: "#1a73e8", fontSize: 20, fontWeight: 700 }}
            >
              {!group.driver?.avatar ? driverInitial : null}
            </Avatar>
            <div style={{ flex: 1 }}>
              <div className={cls.driverName}>{group.driver.name}</div>
              {group.driver.phone && (
                <a href={`tel:${group.driver.phone}`} style={{ textDecoration: "none" }}>
                  <div className={cls.driverPhone}>
                    <PhoneIcon style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }} />
                    {group.driver.phone}
                  </div>
                </a>
              )}
            </div>
            {showStopsButton && (
              <button
                onClick={() => setStopsModalOpen(true)}
                aria-label="Show all stops"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 12px", borderRadius: 10,
                  background: "#e8f0fe", color: "#1a73e8", border: "none",
                  fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                  // HTML buttons don't inherit font-family — match the app theme
                  fontFamily: "DM Sans, Roboto, Helvetica, Arial, sans-serif",
                }}
              >
                <FormatListBulletedIcon style={{ fontSize: 18 }} />
                Stops
              </button>
            )}
          </div>
        )}

        {/* Vehicle */}
        {vehicle && (vehicle.make || vehicle.model || vehicle.color || vehicle.plate) ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <DirectionsCarIcon style={{ fontSize: 16, color: "#666" }} />
              <Typography style={{ fontSize: 12, fontWeight: 600, color: "#666", textTransform: "uppercase" }}>Vehicle</Typography>
            </div>
            <div style={{ marginLeft: 22 }}>
              {[vehicle.color, vehicle.make, vehicle.model].filter(Boolean).map((v, i) => (
                <span key={i} className={cls.vehicleTag}>{v}</span>
              ))}
              {vehicle.plate && <span style={{ fontSize: 13, color: "#555", marginLeft: 4 }}>Plate: {vehicle.plate}</span>}
            </div>
          </div>
        ) : null}

        {/* Service Date */}
        {serviceDate && (
          <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
            <CalendarTodayIcon style={{ fontSize: 14, color: "#1a73e8" }} />
            <Typography style={{ fontSize: 13, fontWeight: 600, color: "#1a73e8" }}>{serviceDate}</Typography>
          </div>
        )}

        <div className={cls.divider} />

        {/* Pickup */}
        {pickup && (
          <div className={cls.addressRow}>
            <RoomIcon className={cls.addressIcon} style={{ color: "#4caf50", fontSize: 20 }} />
            <div style={{ flex: 1 }}>
              <div className={cls.addressLabel}>
                Pickup{pickupTime ? ` · ${pickupTime}` : ""}
              </div>
              <div className={cls.addressText}>{pickup.address || "—"}</div>
            </div>
          </div>
        )}

        {/* Dropoff */}
        {dropoff && (
          <div className={cls.addressRow}>
            <FlagIcon className={cls.addressIcon} style={{ color: "#f44336", fontSize: 20 }} />
            <div style={{ flex: 1 }}>
              <div className={cls.addressLabel}>
                Drop-off{dropoffTime ? ` · ${dropoffTime}` : ""}
              </div>
              <div className={cls.addressText}>{dropoff.address || "—"}</div>
            </div>
          </div>
        )}
        </>
      )}
      </div>

      {/* All Stops Modal — full stop sequence with client, address & time */}
      <Dialog open={stopsModalOpen} onClose={() => setStopsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          style={{
            paddingRight: 8,
            fontFamily: "DM Sans, Roboto, Helvetica, Arial, sans-serif",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ flex: 1 }}>All Stops {stops.length > 0 ? `(${stops.length})` : ""}</span>
            <IconButton
              onClick={() => setStopsModalOpen(false)}
              aria-label="Close"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent
          dividers
          style={{ fontFamily: "DM Sans, Roboto, Helvetica, Arial, sans-serif" }}
        >
          {stops.length === 0 ? (
            <DialogContentText>No stops available.</DialogContentText>
          ) : (
            stops.map((s, idx) => (
              <div
                key={s.id || idx}
                style={{
                  display: "flex", gap: 12, padding: "12px 4px",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                    background: s.type === "pickup" ? "#e8f5e9" : "#fbe9e7",
                    color: s.type === "pickup" ? "#2e7d32" : "#c62828",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700, marginTop: 2,
                  }}
                >
                  {idx + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <Typography style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                      {s.client || "—"}
                    </Typography>
                    <Typography style={{ fontSize: 12, fontWeight: 600, color: s.type === "pickup" ? "#2e7d32" : "#c62828" }}>
                      {s.type === "pickup" ? "Pickup" : "Drop-off"}
                    </Typography>
                  </div>
                  <div style={{ fontSize: 13, color: "#555", marginTop: 2, lineHeight: 1.4 }}>{s.address || "—"}</div>
                  {s.scheduled_time && (
                    <div style={{ fontSize: 12, color: "#1a73e8", marginTop: 2 }}>
                      {fmtTime(s.scheduled_time)}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default TripTracking
